/* ==========================================================================
   cbg-quote.js — theme-only "Request a Quote".

   Loaded globally from sections/header.liquid so the header count badge works
   on every page. Uses localStorage (no app, no backend) and a plain
   `cbg:quote:updated` DOM CustomEvent (does NOT touch constants.js / pubsub).

   Built incrementally:
     - CbgQuote            → the localStorage engine (window.CbgQuote)
     - <cbg-quote-count>   → header / mobile count badge
     - add-to-quote        → delegated clicks on [data-cbg-quote-add] (PDP + Quick View) + toast
     - <cbg-quote-page>    → (added in a later step) the Request a Quote page list + form
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'cbg:quote';
  var EVENT = 'cbg:quote:updated';

  /* ---- Engine ---------------------------------------------------------- */
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode / quota — quote can't persist; still notify listeners this session */
    }
    try {
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: list } }));
    } catch (e) {
      /* CustomEvent unsupported — ignore */
    }
  }

  var CbgQuote = {
    EVENT: EVENT,

    getItems: function () {
      return read();
    },

    getCount: function () {
      return read().reduce(function (n, it) {
        return n + (parseInt(it.qty, 10) || 0);
      }, 0);
    },

    // item = { handle, variantId, qty }. Same variant → quantities merge.
    add: function (item) {
      if (!item || !item.variantId) return;
      var qty = parseInt(item.qty, 10) || 1;
      if (qty < 1) qty = 1;
      var list = read();
      var existing = list.filter(function (it) {
        return String(it.variantId) === String(item.variantId);
      })[0];
      if (existing) {
        existing.qty = (parseInt(existing.qty, 10) || 0) + qty;
      } else {
        list.push({ handle: item.handle || '', variantId: String(item.variantId), qty: qty });
      }
      write(list);
    },

    setQty: function (variantId, qty) {
      var n = parseInt(qty, 10) || 1;
      if (n < 1) n = 1;
      var list = read();
      var found = false;
      list.forEach(function (it) {
        if (String(it.variantId) === String(variantId)) {
          it.qty = n;
          found = true;
        }
      });
      if (found) write(list);
    },

    remove: function (variantId) {
      var list = read().filter(function (it) {
        return String(it.variantId) !== String(variantId);
      });
      write(list);
    },

    clear: function () {
      write([]);
    },

    storageAvailable: function () {
      try {
        var k = '__cbg_quote_test__';
        localStorage.setItem(k, '1');
        localStorage.removeItem(k);
        return true;
      } catch (e) {
        return false;
      }
    },
  };

  window.CbgQuote = CbgQuote;

  // Keep the badge in sync when the list changes in another tab.
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    try {
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: read() } }));
    } catch (err) {
      /* ignore */
    }
  });

  /* ---- Header count badge --------------------------------------------- */
  if (!customElements.get('cbg-quote-count')) {
    customElements.define(
      'cbg-quote-count',
      class CbgQuoteCount extends HTMLElement {
        connectedCallback() {
          this.render = this.render.bind(this);
          this.render();
          document.addEventListener(EVENT, this.render);
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.render);
        }

        render() {
          var count = CbgQuote.getCount();
          this.textContent = count > 0 ? String(count) : '';
          this.hidden = count === 0;
        }
      }
    );
  }

  /* ---- "Added to quote" toast ----------------------------------------- */
  var toastEl = null;
  var toastTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cbg-quote-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    void toastEl.offsetWidth; // reflow so the transition replays
    toastEl.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      if (toastEl) toastEl.classList.remove('is-visible');
    }, 2500);
  }

  /* ---- Add to quote (PDP + Quick View) -------------------------------- */
  // Delegated so it works for the PDP button and the dynamically-loaded
  // Quick View button without per-section handlers.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-cbg-quote-add]');
    if (!btn) return;
    event.preventDefault();

    // Not logged in → Liquid stamped the login URL on the button. Redirect
    // instead of adding to the quote (logged-in buttons carry no such attribute).
    var loginUrl = btn.getAttribute('data-cbg-login-url');
    if (loginUrl) {
      window.location.href = loginUrl;
      return;
    }

    var variantId = null;
    var qty = 1;
    var form = btn.closest('form');
    if (form) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) variantId = idInput.value;
      var qtyInput = form.querySelector(
        '[data-cbg-qty-input], [data-cbg-qv-qty-input], .quantity__input, input[type="number"]'
      );
      if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;
    }
    if (!variantId) variantId = btn.getAttribute('data-quote-variant');
    if (!variantId) return;

    CbgQuote.add({
      handle: btn.getAttribute('data-quote-handle') || '',
      variantId: variantId,
      qty: qty,
    });

    showToast(btn.getAttribute('data-added-text') || 'Added to quote');
  });

  /* ---- Shared helpers for the quote page ------------------------------ */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function predictiveUrl() {
    return (window.routes && window.routes.predictive_search_url) || ROOT + 'search/suggest';
  }

  // Read a product's custom.min_order_quantity metafield client-side. The
  // /products/{handle}.js endpoint omits metafields, so we render the tiny
  // cbg-min-qty section for this product via the Section Rendering API and
  // pull the value out of its data attribute. Falls back to 1 on any error.
  function fetchMinQty(handle) {
    return fetch(ROOT + 'products/' + handle + '?section_id=cbg-min-qty')
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        var m = html.match(/data-cbg-min-qty="(\d+)"/);
        var n = m ? parseInt(m[1], 10) : 1;
        return n > 1 ? n : 1;
      })
      .catch(function () {
        return 1;
      });
  }

  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- Custom accessible select (Country / State) --------------------- */
  if (!customElements.get('cbg-quote-select')) {
    customElements.define(
      'cbg-quote-select',
      class extends HTMLElement {
        connectedCallback() {
          this.select = this.querySelector('select');
          if (!this.select) return;
          this.isOpen = false;
          this.onDocClick = this.onDocClick.bind(this);
          this.build();
          this.classList.add('is-enhanced');
          document.addEventListener('click', this.onDocClick);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
        }

        build() {
          this.button = document.createElement('button');
          this.button.type = 'button';
          this.button.className = 'cbg-raq__select-button';
          this.button.setAttribute('aria-haspopup', 'listbox');
          this.button.setAttribute('aria-expanded', 'false');
          this.labelEl = document.createElement('span');
          var caret = document.createElement('span');
          caret.className = 'cbg-raq__select-caret svg-wrapper';
          caret.setAttribute('aria-hidden', 'true');
          caret.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          this.button.appendChild(this.labelEl);
          this.button.appendChild(caret);

          this.listEl = document.createElement('ul');
          this.listEl.className = 'cbg-raq__select-list';
          this.listEl.setAttribute('role', 'listbox');
          this.listEl.hidden = true;

          this.appendChild(this.button);
          this.appendChild(this.listEl);

          this.button.addEventListener('click', () => this.toggle());
          this.button.addEventListener('keydown', (e) => this.onKey(e));
          this.listEl.addEventListener('click', (e) => this.onPick(e));

          this.refresh();
        }

        refresh() {
          if (!this.select || !this.listEl) return;
          var self = this;
          this.listEl.innerHTML = '';
          Array.prototype.forEach.call(this.select.options, function (opt, i) {
            var li = document.createElement('li');
            li.className = 'cbg-raq__select-option';
            li.setAttribute('role', 'option');
            li.setAttribute('data-value', opt.value);
            li.textContent = opt.textContent;
            if (i === self.select.selectedIndex) li.setAttribute('aria-selected', 'true');
            self.listEl.appendChild(li);
          });
          var sel = this.select.options[this.select.selectedIndex];
          this.labelEl.textContent = sel ? sel.textContent : '';
          this.button.disabled = this.select.disabled;
        }

        toggle(force) {
          this.isOpen = force != null ? force : !this.isOpen;
          this.listEl.hidden = !this.isOpen;
          this.button.setAttribute('aria-expanded', String(this.isOpen));
        }

        onDocClick(e) {
          if (!this.contains(e.target)) this.toggle(false);
        }

        onKey(e) {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(true);
          } else if (e.key === 'Escape') {
            this.toggle(false);
          }
        }

        onPick(e) {
          var li = e.target.closest('.cbg-raq__select-option');
          if (!li) return;
          this.select.value = li.getAttribute('data-value');
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
          this.refresh();
          this.toggle(false);
          this.button.focus();
        }
      }
    );
  }

  /* ---- Request a Quote page ------------------------------------------- */
  if (!customElements.get('cbg-quote-page')) {
    customElements.define(
      'cbg-quote-page',
      class extends HTMLElement {
        connectedCallback() {
          this.itemsEl = this.querySelector('[data-cbg-quote-items]');
          this.emptyEl = this.querySelector('[data-cbg-quote-empty]');
          this.countNum = this.querySelector('[data-cbg-quote-count-num]');
          this.searchInput = this.querySelector('[data-cbg-quote-search]');
          this.resultsEl = this.querySelector('[data-cbg-quote-results]');
          this.addQty = this.querySelector('[data-cbg-quote-add-qty]');
          this.addItemBtn = this.querySelector('[data-cbg-quote-add-item]');
          this.itemsField = this.querySelector('[data-cbg-quote-items-field]');
          this.form = document.getElementById('CbgQuoteForm');
          this.unavailableEl = this.querySelector('[data-cbg-quote-unavailable]');
          this.nostorageEl = this.querySelector('[data-cbg-quote-nostorage]');
          this.successEl = this.querySelector('[data-cbg-quote-success]');

          this.L = {
            item: this.getAttribute('data-label-item') || 'ITEM:',
            qty: this.getAttribute('data-label-qty') || 'Quantity:',
            remove: this.getAttribute('data-label-remove') || 'REMOVE',
            need: this.getAttribute('data-label-need-items') || 'Please add at least one item.',
          };

          this.cache = {}; // handle -> product promise
          this.hydrated = {}; // variantId -> {title, sku, price}
          this.renderedKey = null;
          this.selected = null; // {handle, variantId}
          this.selectedMin = 1; // min_order_quantity of the picked product
          this.searchController = null;
          this.searchDebounce = null;

          // Posted successfully → clear the list + reveal the success message.
          if (this.querySelector('[data-cbg-quote-posted]')) {
            CbgQuote.clear();
            this.classList.add('is-posted');
            if (this.successEl) {
              this.successEl.hidden = false;
              this.successEl.focus();
            }
            return;
          }

          if (!CbgQuote.storageAvailable() && this.nostorageEl) this.nostorageEl.hidden = false;

          this.onQuoteUpdate = this.onQuoteUpdate.bind(this);
          document.addEventListener(EVENT, this.onQuoteUpdate);

          this.bindAddBox();
          this.bindList();
          this.bindCountryState();
          if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

          this.rebuild();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.onQuoteUpdate);
          if (this.searchController) this.searchController.abort();
        }

        keyOf(items) {
          return items
            .map(function (it) {
              return it.variantId;
            })
            .sort()
            .join(',');
        }

        onQuoteUpdate() {
          var items = CbgQuote.getItems();
          if (this.keyOf(items) !== this.renderedKey) this.rebuild();
          else this.updateCount();
        }

        updateCount() {
          var items = CbgQuote.getItems();
          var total = items.reduce(function (n, it) {
            return n + (parseInt(it.qty, 10) || 0);
          }, 0);
          if (this.countNum) this.countNum.textContent = total;
          var empty = items.length === 0;
          this.classList.toggle('is-quote-empty', empty);
          if (this.emptyEl) this.emptyEl.hidden = !empty;
          this.querySelectorAll('.cbg-raq__submit').forEach(function (b) {
            if (empty) b.setAttribute('disabled', 'disabled');
            else b.removeAttribute('disabled');
          });
        }

        hydrate(handle) {
          if (!this.cache[handle]) {
            this.cache[handle] = fetch(ROOT + 'products/' + handle + '.js').then(function (r) {
              return r.ok ? r.json() : null; // null = product gone
            });
          }
          return this.cache[handle];
        }

        rebuild() {
          var self = this;
          var items = CbgQuote.getItems();
          this.renderedKey = this.keyOf(items);
          this.updateCount();
          if (!items.length) {
            if (this.itemsEl) this.itemsEl.innerHTML = '';
            return;
          }
          Promise.all(
            items.map(function (it) {
              if (!it.handle) return Promise.resolve({ it: it, product: null });
              return self.hydrate(it.handle).then(function (p) {
                return { it: it, product: p };
              });
            })
          ).then(function (rows) {
            if (!self.itemsEl) return;
            var gone = [];
            self.itemsEl.innerHTML = '';
            rows.forEach(function (row) {
              if (!row.product) {
                gone.push(row.it.variantId);
                return;
              }
              var node = self.renderRow(row.it, row.product);
              if (node) self.itemsEl.appendChild(node);
            });
            if (gone.length) {
              gone.forEach(function (vid) {
                CbgQuote.remove(vid);
              });
              if (self.unavailableEl) self.unavailableEl.hidden = false;
            }
          });
        }

        renderRow(item, product) {
          var variant =
            (product.variants || []).filter(function (v) {
              return String(v.id) === String(item.variantId);
            })[0] || (product.variants || [])[0];
          if (!variant) return null;

          this.hydrated[item.variantId] = {
            title: product.title,
            sku: variant.sku || '',
            price: money(variant.price),
          };

          var img =
            (variant.featured_image && variant.featured_image.src) ||
            product.featured_image ||
            (product.images && product.images[0]) ||
            '';
          var qty = parseInt(item.qty, 10) || 1;
          var url = product.url || '/products/' + product.handle;

          var wrap = document.createElement('div');
          wrap.className = 'cbg-raq__item';
          wrap.setAttribute('data-variant', item.variantId);
          wrap.innerHTML =
            '<div class="cbg-raq__item-media">' +
            (img ? '<img src="' + img + '" alt="" width="160" height="160" loading="lazy">' : '') +
            '</div>' +
            '<div class="cbg-raq__item-info">' +
            '<a class="cbg-raq__item-title" href="' + url + '">' +
            escapeHtml(product.title) +
            '</a>' +
            '<span class="cbg-raq__item-price">' +
            this.hydrated[item.variantId].price +
            '</span>' +
            (variant.sku
              ? '<span class="cbg-raq__item-sku">' +
                escapeHtml(this.L.item) +
                ' <span class="cbg-raq__item-sku-value">' +
                escapeHtml(variant.sku) +
                '</span></span>'
              : '') +
            '</div>' +
            '<div class="cbg-raq__item-qtycell">' +
            '<span class="cbg-raq__qty-label">' +
            escapeHtml(this.L.qty) +
            '</span>' +
            '<div class="cbg-raq__qty">' +
            '<button type="button" class="cbg-raq__step" data-step="minus" aria-label="−">&minus;</button>' +
            '<input type="number" class="cbg-raq__input cbg-raq__qty-input" min="1" inputmode="numeric" value="' +
            qty +
            '" data-row-qty>' +
            '<button type="button" class="cbg-raq__step" data-step="plus" aria-label="+">&plus;</button>' +
            '</div>' +
            '</div>' +
            '<div class="cbg-raq__item-removecell">' +
            '<button type="button" class="cbg-raq__remove" data-row-remove>' +
            escapeHtml(this.L.remove) +
            '</button>' +
            '</div>';
          return wrap;
        }

        bindList() {
          var self = this;
          if (!this.itemsEl) return;
          this.itemsEl.addEventListener('click', function (e) {
            var row = e.target.closest('[data-variant]');
            if (!row) return;
            var vid = row.getAttribute('data-variant');
            var step = e.target.closest('[data-step]');
            if (step) {
              var input = row.querySelector('[data-row-qty]');
              var val = parseInt(input.value, 10) || 1;
              val = step.getAttribute('data-step') === 'minus' ? Math.max(1, val - 1) : val + 1;
              input.value = val;
              CbgQuote.setQty(vid, val);
              return;
            }
            if (e.target.closest('[data-row-remove]')) CbgQuote.remove(vid);
          });
          this.itemsEl.addEventListener('change', function (e) {
            var input = e.target.closest('[data-row-qty]');
            if (!input) return;
            var row = e.target.closest('[data-variant]');
            var val = Math.max(1, parseInt(input.value, 10) || 1);
            input.value = val;
            CbgQuote.setQty(row.getAttribute('data-variant'), val);
          });
        }

        bindAddBox() {
          var self = this;
          if (this.searchInput) this.searchInput.addEventListener('input', function () { self.onSearchInput(); });
          if (this.resultsEl) this.resultsEl.addEventListener('click', function (e) { self.onResultPick(e); });
          this.querySelectorAll('[data-cbg-quote-step]').forEach(function (b) {
            b.addEventListener('click', function () {
              if (!self.addQty) return;
              var floor = self.selectedMin || 1;
              var val = parseInt(self.addQty.value, 10) || floor;
              val = b.getAttribute('data-cbg-quote-step') === 'minus' ? Math.max(floor, val - 1) : val + 1;
              self.addQty.value = val;
            });
          });
          if (this.addItemBtn) this.addItemBtn.addEventListener('click', function () { self.onAddItem(); });
        }

        onSearchInput() {
          var self = this;
          this.selected = null;
          this.selectedMin = 1;
          if (this.addQty) this.addQty.min = 1;
          var q = this.searchInput.value.trim();
          if (this.searchDebounce) clearTimeout(this.searchDebounce);
          if (q.length < 2) {
            this.clearResults();
            return;
          }
          this.searchDebounce = setTimeout(function () { self.search(q); }, 250);
        }

        search(q) {
          var self = this;
          if (this.searchController) this.searchController.abort();
          this.searchController = new AbortController();
          var url =
            predictiveUrl() +
            '.json?q=' +
            encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=8' +
            '&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku';
          fetch(url, { signal: this.searchController.signal })
            .then(function (r) { return r.json(); })
            .then(function (data) {
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              self.renderResults(products);
            })
            .catch(function () {});
        }

        renderResults(products) {
          if (!this.resultsEl) return;
          if (!products.length) {
            this.clearResults();
            return;
          }
          this.resultsEl.innerHTML = products
            .map(function (p) {
              return (
                '<li><button type="button" class="cbg-raq__result" data-handle="' +
                escapeHtml(p.handle || '') +
                '">' +
                escapeHtml(p.title || '') +
                '</button></li>'
              );
            })
            .join('');
          this.resultsEl.hidden = false;
        }

        clearResults() {
          if (this.resultsEl) {
            this.resultsEl.innerHTML = '';
            this.resultsEl.hidden = true;
          }
        }

        onResultPick(e) {
          var self = this;
          var btn = e.target.closest('.cbg-raq__result');
          if (!btn) return;
          var handle = btn.getAttribute('data-handle');
          var q = this.searchInput.value.trim().toLowerCase();
          Promise.all([
            fetch(ROOT + 'products/' + handle + '.js').then(function (r) { return r.json(); }),
            fetchMinQty(handle),
          ])
            .then(function (res) {
              var product = res[0];
              var minQty = res[1];
              var variants = product.variants || [];
              var match = variants.filter(function (v) { return v.sku && v.sku.toLowerCase() === q; })[0];
              var avail = variants.filter(function (v) { return v.available; })[0];
              var chosen = match || avail || variants[0];
              if (!chosen) return;
              self.selected = { handle: handle, variantId: chosen.id };
              self.selectedMin = minQty > 1 ? minQty : 1;
              self.searchInput.value = product.title;
              if (self.addQty) {
                self.addQty.min = self.selectedMin;
                self.addQty.value = self.selectedMin;
              }
              self.clearResults();
            })
            .catch(function () {});
        }

        onAddItem() {
          if (!this.selected) return;
          var floor = this.selectedMin || 1;
          var qty = parseInt(this.addQty && this.addQty.value, 10) || floor;
          if (qty < floor) qty = floor;
          CbgQuote.add({ handle: this.selected.handle, variantId: this.selected.variantId, qty: qty });
          this.selected = null;
          this.selectedMin = 1;
          if (this.searchInput) this.searchInput.value = '';
          if (this.addQty) {
            this.addQty.value = 1;
            this.addQty.min = 1;
          }
        }

        bindCountryState() {
          var country = this.querySelector('[data-cbg-quote-country]');
          var state = this.querySelector('[data-cbg-quote-state]');
          if (!country || !state) return;
          var stateComp = state.closest('cbg-quote-select');
          var self = this;
          this.stateSelectLabel = state.options[0] ? state.options[0].textContent : '-- Select --';

          function populate() {
            var opt = country.options[country.selectedIndex];
            var provinces = [];
            try {
              provinces = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]');
            } catch (e) {
              provinces = [];
            }
            state.innerHTML = '';
            var first = document.createElement('option');
            first.value = '';
            first.textContent = self.stateSelectLabel;
            state.appendChild(first);
            provinces.forEach(function (p) {
              var val = (p && (p[1] || p[0])) || '';
              var o = document.createElement('option');
              o.value = val;
              o.textContent = val;
              state.appendChild(o);
            });
            state.disabled = provinces.length === 0;
            if (stateComp && typeof stateComp.refresh === 'function') stateComp.refresh();
          }

          populate();
          country.addEventListener('change', populate);
        }

        onSubmit(e) {
          var self = this;
          var items = CbgQuote.getItems();
          if (!items.length) {
            e.preventDefault();
            this.updateCount();
            if (this.emptyEl) this.emptyEl.hidden = false;
            return;
          }
          var lines = items.map(function (it) {
            var h = self.hydrated[it.variantId];
            var qty = parseInt(it.qty, 10) || 1;
            if (h) return qty + ' x ' + h.title + (h.sku ? ' (SKU ' + h.sku + ')' : '') + ' - ' + h.price;
            return qty + ' x ' + (it.handle || 'variant ' + it.variantId);
          });
          if (this.itemsField) this.itemsField.value = lines.join('\n');
          // native submit proceeds
        }
      }
    );
  }
})();
